'use client';

/**
 * OrganizationConnectionsTab Component
 *
 * Main tab content for managing organization connections.
 * Displays connected providers and allows adding new connections.
 *
 * @example
 * ```tsx
 * <OrganizationConnectionsTab organizationId="org-123" />
 * ```
 */

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  Center,
  Grid,
  Inline,
  Spinner,
  Stack,
  Text,
} from '@cortex/ui';
import { Plus, Unplug } from 'lucide-react';
import * as React from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

import { AddConnectionModal } from './AddConnectionModal';
import { ConnectionCard } from './ConnectionCard';
import { ManageConnectionModal } from './ManageConnectionModal';

interface OrganizationConnectionsTabProps {
  /**
   * Organization ID to fetch connections for
   */
  organizationId: string;
}

type Connection = {
  id: string;
  label: string;
  providerId: string;
  providerType: string;
  metadata: {
    github_user?: {
      login: string;
      avatar_url: string;
    };
    expires_at?: string;
    refresh_expires_at?: string;
  };
  validationStatus: string | null;
  linkedProjectCount?: number;
  linkedProjectIds?: string[];
};

export function OrganizationConnectionsTab({
  organizationId,
}: OrganizationConnectionsTabProps) {
  const fetchWithAuth = useFetchWithAuth();
  const [connections, setConnections] = React.useState<Connection[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Modal states
  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const [manageModalOpen, setManageModalOpen] = React.useState(false);
  const [selectedConnection, setSelectedConnection] =
    React.useState<Connection | null>(null);
  // Used for disconnect loading state (disconnectingId read for future loading indicator)
  const [, setDisconnectingId] = React.useState<string | null>(null);

  // Fetch connections
  const fetchConnections = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `/admin/organizations/${organizationId}/connections`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch connections');
      }

      const { data } = await response.json();
      // Transform snake_case API response to camelCase
      const transformed: Connection[] = (data?.connections || []).map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (conn: any) => ({
          id: conn.id,
          label: conn.label,
          providerId: conn.provider_id,
          providerType: conn.provider_type,
          metadata: conn.metadata || {},
          validationStatus: conn.validation_status,
          linkedProjectCount: conn.linked_project_count ?? 0,
          linkedProjectIds: conn.linked_project_ids ?? [],
        })
      );
      setConnections(transformed);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load connections'
      );
    } finally {
      setLoading(false);
    }
  }, [organizationId, fetchWithAuth]);

  React.useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const handleManage = (connectionId: string) => {
    const connection = connections.find((c) => c.id === connectionId);
    if (connection) {
      setSelectedConnection(connection);
      setManageModalOpen(true);
    }
  };

  const handleDisconnect = async (connectionId: string) => {
    if (!confirm('Are you sure you want to disconnect this connection?')) {
      return;
    }

    setDisconnectingId(connectionId);

    try {
      const response = await fetchWithAuth(
        `/admin/connections/${connectionId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok && response.status !== 204) {
        throw new Error('Failed to disconnect');
      }

      // Refresh connections list
      await fetchConnections();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to disconnect connection'
      );
    } finally {
      setDisconnectingId(null);
    }
  };

  if (loading) {
    return (
      <Center minHeight="12rem">
        <Spinner size="md" variant="muted" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchConnections}
          className="mt-2"
        >
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Inline justify="between">
        <Stack gap="none">
          <Text size="lg" weight="semibold">
            Connections
          </Text>
          <Text color="muted" size="sm" className="mt-1">
            Manage integrations with external providers
          </Text>
        </Stack>
        <Button onClick={() => setAddModalOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Connection
        </Button>
      </Inline>

      {/* Connections Grid */}
      {connections.length === 0 ? (
        <Card className="flex flex-col items-center justify-center border-dashed py-12">
          <Center className="bg-surface-3 mb-4 h-16 w-16 rounded-full">
            <Unplug className="text-foreground/60 h-8 w-8" />
          </Center>
          <Text weight="semibold" className="mb-2">
            No connections configured
          </Text>
          <Text
            color="muted"
            size="sm"
            align="center"
            className="mb-4 max-w-md"
          >
            Connect your organization to providers like GitHub to enable
            automated workflows
          </Text>
          <Button onClick={() => setAddModalOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Connect GitHub
          </Button>
        </Card>
      ) : (
        <Grid gap="md" className="sm:grid-cols-2 lg:grid-cols-3">
          {connections.map((connection) => (
            <ConnectionCard
              key={connection.id}
              connection={connection}
              organizationId={organizationId}
              onManage={handleManage}
              onDisconnect={handleDisconnect}
            />
          ))}
        </Grid>
      )}

      {/* Add Connection Modal */}
      <AddConnectionModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        organizationId={organizationId}
        onSuccess={fetchConnections}
      />

      {/* Manage Connection Modal */}
      <ManageConnectionModal
        open={manageModalOpen}
        onClose={() => {
          setManageModalOpen(false);
          setSelectedConnection(null);
        }}
        connection={selectedConnection}
        organizationId={organizationId}
        onUpdate={fetchConnections}
      />
    </Stack>
  );
}
